/* istanbul ignore file */

import { Module } from 'vuex';
import { AppState } from '@/store';
import { CustomerDeliveryAddress, OrderWithContext, DeliveryEstimate, OrderDeliveryInfo, OrderMeal, Restaurant } from '@/models';
import { prefixValues } from '@/helpers';
import { CharitableCampaign } from '@/models/donations';
import { FeedbackResponse } from '@/models/feedback';

export interface OrderModuleState {
  pendingOrder: OrderWithContext | null;
  pendingOrderDeliveryInfo: OrderDeliveryInfo | null;

  selectedDeliveryAddress: CustomerDeliveryAddress | null;
  cachedDeliveryAddress: CustomerDeliveryAddress | null;
  cachedRestaurant: Restaurant | null;

  pendingGroupOrder: OrderWithContext | null;
  groupOrderParticpantName: string;
  groupOrderMealIds: string[];
  isGroupOrderHost: boolean;
  isGroupOrderParticipant: boolean;

  lastOrder: {
    order: OrderWithContext | null;
    estimate: DeliveryEstimate | null;
  };

  /** We have been requested to treat upsell items as a separate meal, while not showing that meal in the bag.
   * So, we need to track upsell meal specially.
   */
  upsellMeal: OrderMeal | null;

  charitableCampaign: CharitableCampaign | null;
  donationAmount: number;
  feedback: FeedbackResponse | null;
  promoCode: string;
  promoErrorMessage: string;
  lastRetrievalType: string | null;
}

const MUTATION_NAMES = {
  SET_PROMO_CODE: 'setPromoCode',
  SET_PROMO_ERROR_MESSAGE: 'setPromoErrorMessage',
  SET_SELECTED_DELIVERY_ADDRESS: 'setSelectedDeliveryAddress',
  SET_CACHED_DELIVERY_ADDRESS: 'setCachedDeliveryAddress',
  SET_CACHED_RESTAURANT: 'setCachedRestaurant',
  SET_ORDER: 'setOrder',
  SET_ORDER_PHONE_NUMBER: 'setOrderPhoneNumber',
  SET_ORDER_DELIVERY_INFO: 'setOrderDeliveryInfo',
  SET_LAST_ORDER: 'setLastOrder',
  SET_GROUP_ORDER: 'setGroupOrder',
  SET_GROUP_ORDER_PARTICIPANT_NAME: 'setGroupOrderParticipantName',
  SET_GROUP_ORDER_MEAL_IDS: 'setGroupOrderMealIds',
  SET_IS_GROUP_ORDER_HOST: 'setIsGroupOrderHost',
  SET_IS_GROUP_ORDER_PARTICIPANT: 'setIsGroupOrderParticipant',
  SET_UPSELL_MEAL: 'setUpsellMeal',
  SET_CHARITABLE_CAMPAIGN: 'setCharitableCampaign',
  SET_DONATION_AMOUNT: 'setDonationAmount',
  SET_FEEDBACK: 'setFeedback',
  SET_LAST_RETRIEVAL_TYPE: 'setLastRetrievalType',
};

export const MUTATIONS = prefixValues(MUTATION_NAMES, 'order/');

const DEFAULT_STATE: OrderModuleState = {
  selectedDeliveryAddress: null,
  cachedDeliveryAddress: null,
  cachedRestaurant:null,
  pendingOrder: null,
  pendingOrderDeliveryInfo: null,
  pendingGroupOrder: null,
  groupOrderParticpantName: '',
  groupOrderMealIds: [],
  isGroupOrderHost: false,
  isGroupOrderParticipant: false,
  lastOrder: {
    order: null,
    estimate: null,
  },
  upsellMeal: null,
  charitableCampaign: null,
  donationAmount: 0,
  feedback: null,
  promoCode: '',
  promoErrorMessage: '',
  lastRetrievalType: null,
};

export function makeOrderModule(): Module<OrderModuleState, AppState> {
  return {
    namespaced: true,
    state: DEFAULT_STATE,
    mutations: {
      [MUTATION_NAMES.SET_CACHED_RESTAURANT]: (state, restaurant: Restaurant) => {
        state.cachedRestaurant = restaurant;
      },
      [MUTATION_NAMES.SET_PROMO_CODE]: (state, promoCode) => {
        state.promoCode = promoCode;
      },
      [MUTATION_NAMES.SET_PROMO_ERROR_MESSAGE]: (state, promoErrorMessage) => {
        state.promoErrorMessage = promoErrorMessage;
      },
      [MUTATION_NAMES.SET_SELECTED_DELIVERY_ADDRESS]: (state, selectedDeliveryAddress) => {
        state.selectedDeliveryAddress = selectedDeliveryAddress;
      },
      [MUTATION_NAMES.SET_CACHED_DELIVERY_ADDRESS]: (state, cachedDeliveryAddress) => {
        state.cachedDeliveryAddress = cachedDeliveryAddress;
      },
      [MUTATION_NAMES.SET_ORDER]: (state, order: OrderWithContext) => {
        state.pendingOrder = order;
      },
      [MUTATION_NAMES.SET_ORDER_DELIVERY_INFO]: (state, orderDeliveryInfo: OrderDeliveryInfo) => {
        state.pendingOrderDeliveryInfo = orderDeliveryInfo;
      },
      [MUTATION_NAMES.SET_ORDER_PHONE_NUMBER]: (state, phoneNumber: string) => {
        if (state.selectedDeliveryAddress) {
          state.selectedDeliveryAddress.phoneNumber = phoneNumber.replace(/\D/g, '');
        }
      },
      [MUTATION_NAMES.SET_GROUP_ORDER]: (state, order: OrderWithContext) => {
        state.pendingGroupOrder = order;
      },
      [MUTATION_NAMES.SET_GROUP_ORDER_PARTICIPANT_NAME]: (state, name: string) => {
        state.groupOrderParticpantName = name;
      },
      [MUTATION_NAMES.SET_GROUP_ORDER_MEAL_IDS]: (state, mealIds: string[]) => {
        state.groupOrderMealIds = mealIds;
      },
      [MUTATION_NAMES.SET_IS_GROUP_ORDER_HOST]: (state, isGroupOrderHost: boolean) => {
        state.isGroupOrderHost = isGroupOrderHost;
      },
      [MUTATION_NAMES.SET_IS_GROUP_ORDER_PARTICIPANT]: (state, isGroupOrderParticipant: boolean) => {
        state.isGroupOrderParticipant = isGroupOrderParticipant;
      },
      [MUTATION_NAMES.SET_LAST_ORDER]: (state, args: { order: OrderWithContext; estimate: DeliveryEstimate | null }) => {
        state.lastOrder.order = args.order;
        state.lastOrder.estimate = args.estimate;
      },
      [MUTATION_NAMES.SET_UPSELL_MEAL]: (state, upsellMeal: OrderMeal | null) => {
        state.upsellMeal = upsellMeal;
      },
      [MUTATION_NAMES.SET_CHARITABLE_CAMPAIGN]: (state, charitableCampaign: CharitableCampaign | null) => {
        state.charitableCampaign = charitableCampaign;
      },
      [MUTATION_NAMES.SET_DONATION_AMOUNT]: (state, donationAmount: number) => {
        state.donationAmount = donationAmount;
      },
      [MUTATION_NAMES.SET_FEEDBACK]: (state, feedback: FeedbackResponse) => {
        state.feedback = feedback;
      },
      [MUTATION_NAMES.SET_LAST_RETRIEVAL_TYPE]: (state, retrievalType: string) => {
        state.lastRetrievalType = retrievalType;
      },
    },
  };
}
